<script setup>
import { produktnavn } from '@/Composables/produktnavn';
import { computed } from 'vue';

const props = defineProps({
    product: {
        type: Object,
        required: true,
    },
});

const thumbnailUrl = computed(() => {
    if (props.product.images && props.product.images.length > 0) {
        return `/images/${props.product.images[0].id}`;
    }
    return null;
});

const formattedPrice = computed(() => {
    const value = Number(props.product.prices?.lowest_price_including_shipping);
    if (isNaN(value)) return '';
    return new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: 'DKK',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
});

const retailerCount = computed(() => props.product.prices?.total_retailers ?? 0);

const stockStatus = computed(() => {
    const lowestHasStock = props.product.prices?.lowest_price_has_in_stock;
    const othersHaveStock = props.product.prices?.others_has_in_stock;
    if (lowestHasStock || othersHaveStock) {
        return { inStock: true, label: 'På lager', icon: '✓' };
    }
    return { inStock: false, label: 'Ikke på lager', icon: '✗' };
});

const brandName = computed(() => props.product.brand?.name ?? '');
</script>

<template>
    <a :href="product.route_to_productdetail" class="product-grid-card">
        <div class="card-image">
            <picture v-if="thumbnailUrl">
                <source :srcset="`${thumbnailUrl}?w=320&fmt=webp`" type="image/webp" />
                <img
                    :src="`${thumbnailUrl}?w=320`"
                    :alt="produktnavn(product)"
                    class="product-img"
                    width="160"
                    height="160"
                    loading="lazy"
                    decoding="async"
                />
            </picture>
            <div v-else class="img-placeholder"></div>
        </div>

        <div class="card-info">
            <span class="brand-label">{{ brandName }}</span>
            <span class="product-name">{{ produktnavn(product) }}</span>

            <div class="card-bottom">
                <span class="price">{{ formattedPrice }}</span>
                <span class="meta">
                    <span class="retailer-count">🛒 {{ retailerCount }}</span>
                    <span :class="['stock-status', stockStatus.inStock ? 'in-stock' : 'out-of-stock']">
                        {{ stockStatus.icon }} {{ stockStatus.label }}
                    </span>
                </span>
            </div>
        </div>
    </a>
</template>

<style scoped>
.product-grid-card {
    display: flex;
    flex-direction: column;
    background: var(--pc-bg-secondary, #1a1a2e);
    border: 1px solid var(--pc-border, #2a2a4a);
    border-radius: var(--pc-radius-lg, 12px);
    overflow: hidden;
    text-decoration: none;
    color: var(--pc-text-primary, #e0e0ff);
    transition: all var(--pc-transition, 150ms) ease;
    border-top: 3px solid transparent;
}

.product-grid-card:hover {
    transform: translateY(-2px);
    box-shadow: var(--pc-shadow-lift, 0 8px 24px rgba(0, 0, 0, 0.3));
    border-top-color: var(--pc-accent, #6366f1);
}

.card-image {
    height: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f0f0f0;
    padding: 16px;
}

.product-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.img-placeholder {
    width: 64px;
    height: 64px;
    border-radius: 8px;
    background: var(--pc-bg-secondary, #1a1a2e);
}

.card-info {
    display: flex;
    flex-direction: column;
    padding: 16px;
    gap: 4px;
    flex: 1;
}

.brand-label {
    font-size: 11px;
    text-transform: uppercase;
    color: var(--pc-text-muted, #555577);
    letter-spacing: 0.5px;
}

.product-name {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.card-bottom {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-top: auto;
    padding-top: 8px;
}

.price {
    font-size: 18px;
    font-weight: 700;
    color: var(--pc-price, #60a5fa);
    font-variant-numeric: tabular-nums;
}

.meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    font-size: 11px;
}

.retailer-count {
    color: var(--pc-text-secondary, #8888aa);
}

.stock-status {
    font-weight: 500;
}

.stock-status.in-stock {
    color: var(--pc-success, #22c55e);
}

.stock-status.out-of-stock {
    color: var(--pc-danger, #ef4444);
}
</style>
